/* Writing the collapse setting: the pure decision, and the one dispatch that applies it.
 *
 * Separated because the decision is the part with a wrong answer in it. Closing a group is
 * appending an id; opening one is not always removing an id, and that asymmetry is where the
 * relationships view had to grow a special case too.
 */

import { ViewIds } from '@ardoq/api-types';
import { WILDCARD } from '@ardoq/common-helpers';
import { handleStoreSetting } from 'viewSettings/settingsHelper';

export type ToggleCollapsedGroupArgs = {
  collapsedGroupIds: readonly string[];
  /** Every group that could be closed, which a stored wildcard has to be expanded against. */
  collapsibleGroupIds: readonly string[];
  groupId: string;
};

/**
 * The setting after a reader has opened or closed one group, or `null` for nothing to do.
 *
 * Three cases, not two. A stored wildcard is not a list with every id in it - it is a standing
 * instruction that covers groups the reader has never seen, which is what makes it the right
 * thing to store for Collapse all - so opening one group out of it cannot be done by removing an
 * entry. It has to be written out as every group except that one, and from that point the
 * setting is an explicit list again.
 *
 * `null` rather than the list unchanged, for an id that is not a group. A value-equal copy is not
 * a no-op downstream: `handleStoreSetting` writes it, `viewSettings$` emits, and `toDataSelection`
 * compares `collapsedGroupIds` by reference - so a fresh array with the same entries in it gets
 * past `distinctUntilChanged` and rebuilds the graph. Double-clicking a plain component, which is
 * the first thing anyone tries in a 3D view, re-solved the whole layout and cleared the hover to
 * do nothing visible. Saying so in the return type is what keeps a later caller from writing it
 * again.
 */
export const toggleCollapsedGroup = ({
  collapsedGroupIds,
  collapsibleGroupIds,
  groupId,
}: ToggleCollapsedGroupArgs): string[] | null => {
  if (!collapsibleGroupIds.includes(groupId)) return null;

  if (collapsedGroupIds.includes(WILDCARD)) {
    return collapsibleGroupIds.filter(id => id !== groupId);
  }

  return collapsedGroupIds.includes(groupId)
    ? collapsedGroupIds.filter(id => id !== groupId)
    : [...collapsedGroupIds, groupId];
};

/** The setting after Collapse all, or `null` when every group is already collapsed. */
export const toCollapseAll = (
  collapsedGroupIds: readonly string[]
): string[] | null =>
  collapsedGroupIds.includes(WILDCARD) ? null : [WILDCARD];

/** The setting after Expand all, or `null` when every group is already expanded. */
export const toExpandAll = (
  collapsedGroupIds: readonly string[]
): string[] | null => (collapsedGroupIds.length === 0 ? null : []);

/**
 * Writes the setting, which is the whole of the operation.
 *
 * No companion action, unlike the flexible block diagram's buttons. That view has an aq-blocks
 * element already on screen holding its own fold state, so the setting alone would leave the two
 * disagreeing; this one rebuilds the graph from the setting and has nothing else to tell.
 */
export const storeCollapsedGroupIds = (collapsedGroupIds: string[]): void =>
  handleStoreSetting(ViewIds.NETWORK_3D, { collapsedGroupIds });

/** Writes a setting change, or nothing for `null`. */
export const storeCollapsedGroupChange = (
  collapsedGroupIds: string[] | null
): void => {
  if (collapsedGroupIds === null) return;
  storeCollapsedGroupIds(collapsedGroupIds);
};
